import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LayoutService } from 'src/app/layout/service/app.layout.service';
import { AuthService } from '../services/auth.service';
import { LanguageService } from '../services/language.service';
import { MenuService } from '../services/menu.service';

interface UnitGroup {
    organisationName: string;
    units: any[];
}

@Component({
    selector: 'app-unit-switcher-palette',
    templateUrl: './app.unit-switcher-palette.component.html',
    styleUrls: ['./app.unit-switcher-palette.component.scss']
})
export class AppUnitSwitcherPaletteComponent implements OnInit, OnDestroy {
    @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
    @ViewChild('resultsList') resultsList!: ElementRef<HTMLElement>;

    filterValue = '';
    salesLocations: any[] = [];
    groupedUnits: UnitGroup[] = [];
    flatFiltered: any[] = [];
    selectedIndex = 0;
    switching = false;

    private subscriptions = new Subscription();
    /** Prevents mouseenter from stealing selection while arrow-key scrolling. */
    private ignoreHoverUntil = 0;

    constructor(
        public layoutService: LayoutService,
        public languageProvider: LanguageService,
        private authService: AuthService,
        private menuService: MenuService,
        private router: Router
    ) {}

    get visible(): boolean {
        return this.layoutService.state.unitSwitcherPaletteVisible;
    }

    ngOnInit() {
        this.loadLocations();
        this.subscriptions.add(
            this.authService.currentUser$.subscribe(() => {
                this.loadLocations();
                this.applyFilter();
            })
        );
        this.subscriptions.add(
            this.layoutService.unitSwitcherPaletteOpen$.subscribe(() => this.onOpened())
        );
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    @HostListener('document:keydown', ['$event'])
    onDocumentKeydown(event: KeyboardEvent) {
        const isShortcut = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k';
        if (isShortcut) {
            event.preventDefault();
            if (this.visible) {
                this.close();
            } else {
                this.layoutService.showUnitSwitcherPalette();
            }
            return;
        }

        if (!this.visible) {
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            this.close();
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.moveSelection(1);
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.moveSelection(-1);
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            const unit = this.flatFiltered[this.selectedIndex];
            if (unit) {
                this.selectUnit(unit);
            }
        }
    }

    close() {
        this.layoutService.hideUnitSwitcherPalette();
        this.filterValue = '';
        this.selectedIndex = 0;
    }

    navigateToUnitSelection() {
        this.close();
        this.router.navigateByUrl('/unit-selection');
    }

    applyFilter() {
        const filterValue = this.filterValue.trim().toLowerCase();
        const filtered = !filterValue
            ? this.salesLocations
            : this.salesLocations.filter((location) =>
                  location.salesLocationName?.toLowerCase().includes(filterValue) ||
                  String(location?.salesLocationRestaurantId ?? '')
                      .toLowerCase()
                      .includes(filterValue) ||
                  location?.salesLocationRoute?.toLowerCase().includes(filterValue) ||
                  location?.organisationName?.toLowerCase().includes(filterValue)
              );

        this.groupedUnits = this.groupByOrganisation(filtered);
        this.flatFiltered = this.groupedUnits.flatMap((group) => group.units);
        this.selectedIndex = this.flatFiltered.length
            ? Math.min(this.selectedIndex, this.flatFiltered.length - 1)
            : 0;
        setTimeout(() => this.scrollSelectedIntoView());
    }

    setActiveUnit(unit: any) {
        if (Date.now() < this.ignoreHoverUntil) {
            return;
        }
        const index = this.flatFiltered.indexOf(unit);
        if (index >= 0) {
            this.selectedIndex = index;
        }
    }

    isSelected(unit: any): boolean {
        const current = this.authService.currentSalesLocationValue;
        return !!current && current.salesLocationRoute === unit.salesLocationRoute;
    }

    isActiveIndex(unit: any): boolean {
        return this.flatFiltered[this.selectedIndex] === unit;
    }

    async selectUnit(unit: any) {
        if (this.switching) {
            return;
        }

        const current = this.authService.currentSalesLocationValue;
        if (current?.salesLocationRoute === unit.salesLocationRoute) {
            this.close();
            return;
        }

        this.switching = true;
        try {
            const currentRoute = current?.salesLocationRoute ?? '';
            let append = '';
            if (currentRoute) {
                const parts = this.router.url.split(currentRoute);
                parts.shift();
                append = this.menuService.resolveRouteAppend(parts.join(''));
            }

            await this.authService.assignRole(
                this.authService.currentUserSubject.value.uid,
                unit.salesLocationId
            );
            this.close();
            this.router.navigate([`/u/${unit.salesLocationRoute}${append}`]);
        } finally {
            this.switching = false;
        }
    }

    private onOpened() {
        this.loadLocations();
        this.filterValue = '';
        this.applyFilter();
        const currentRoute = this.authService.currentSalesLocationValue?.salesLocationRoute;
        const currentIndex = this.flatFiltered.findIndex(
            (unit) => unit.salesLocationRoute === currentRoute
        );
        this.selectedIndex = currentIndex >= 0 ? currentIndex : 0;
        setTimeout(() => {
            this.searchInput?.nativeElement?.focus({ preventScroll: true });
            this.scrollSelectedIntoView();
        });
    }

    private loadLocations() {
        const data = this.authService.currentUserValue?.data ?? [];
        this.salesLocations = data.filter(
            (x) => x.salesLocationName && x.salesLocationRoute
        );
    }

    private groupByOrganisation(units: any[]): UnitGroup[] {
        const map = new Map<string, any[]>();
        for (const unit of units) {
            const key = unit.organisationName?.trim() || '—';
            if (!map.has(key)) {
                map.set(key, []);
            }
            map.get(key)!.push(unit);
        }

        return Array.from(map.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([organisationName, groupUnits]) => ({
                organisationName,
                units: groupUnits.sort((a, b) =>
                    a.salesLocationName.localeCompare(b.salesLocationName)
                )
            }));
    }

    private moveSelection(delta: number) {
        if (!this.flatFiltered.length) {
            return;
        }

        this.ignoreHoverUntil = Date.now() + 150;
        const count = this.flatFiltered.length;
        this.selectedIndex = (this.selectedIndex + delta + count) % count;

        const input = this.searchInput?.nativeElement;
        if (input && document.activeElement !== input) {
            input.focus({ preventScroll: true });
        }

        setTimeout(() => this.scrollSelectedIntoView(delta));
    }

    private scrollSelectedIntoView(delta?: number) {
        const list = this.resultsList?.nativeElement;
        if (!list) {
            return;
        }
        const active = list.querySelector('.unit-item.active') as HTMLElement | null;
        if (!active) {
            return;
        }

        if (delta === 1 && this.selectedIndex === 0) {
            list.scrollTop = 0;
            return;
        }
        if (delta === -1 && this.selectedIndex === this.flatFiltered.length - 1) {
            list.scrollTop = list.scrollHeight;
            return;
        }

        const listRect = list.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();

        if (activeRect.top < listRect.top) {
            list.scrollTop -= listRect.top - activeRect.top;
        } else if (activeRect.bottom > listRect.bottom) {
            list.scrollTop += activeRect.bottom - listRect.bottom;
        }
    }
}
